/**
 * §4.11 — An `UPDATE ... WHERE id = ?` against the PowerSync VIEW can
 * match ZERO rows and return "success" while the freshly created row is still
 * traveling to the server: the view is rebuilt from the checkpoint just
 * when the local CRUD queue drains. The window is not "X ms passed" —
 * it is "the queue drained midway" — so it cannot be detected
 * afterwards by looking at timers: you must RE-READ and compare.
 *
 * `writeVerified` adapts the canon `patchRowVerified` (listto-app@9ae347b6):
 * write → reread → compare → on mismatch, retry with backoff → exhaust
 * the budget → throw. Never returns silent success.
 *
 * `waitForRowsPresent` adapts `waitForRowPresent`: for the case where an
 * UPDATE goes inside a transaction that depends on a row already existing
 * in the view (e.g. a row created by a PREVIOUS mutation that has not yet
 * come down from the checkpoint), wait for it to appear BEFORE opening the transaction
 * rather than letting the correlated UPDATE match zero rows inside it.
 */

export interface RowVerifyOptions {
  /** Total budget (ms) of retries before throwing. Default 6000. */
  budgetMs?: number;
  /** Base delay (ms) between retries; doubles each time (capped at MAX_DELAY_MS). */
  baseDelayMs?: number;
}

const DEFAULT_BUDGET_MS = 6000;
const DEFAULT_BASE_DELAY_MS = 250;
const MAX_DELAY_MS = 1500;

const sleep = async (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

function nextDelay(attempt: number, baseDelayMs: number): number {
  return Math.min(baseDelayMs * 2 ** attempt, MAX_DELAY_MS);
}

/**
 * Runs `write` (an UPDATE by primary key, or a whole transaction containing
 * one or more) and re-reads with `verify` to CONFIRM that the local view
 * truly reflects the change. If not, it retries (`write` is idempotent on
 * every mutation in this package: it sets absolute values, not
 * increments) with exponential backoff until `options.budgetMs` is exhausted,
 * then throws instead of returning success with the write lost.
 */
export async function writeVerified<Row>(params: {
  write: () => Promise<void>;
  reread: () => Promise<Row[]>;
  verify: (rows: Row[]) => boolean;
  describe: string;
  options?: RowVerifyOptions;
}): Promise<void> {
  const budgetMs = params.options?.budgetMs ?? DEFAULT_BUDGET_MS;
  const baseDelayMs = params.options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const deadline = Date.now() + budgetMs;
  let attempt = 0;

  // The loop is DELIBERATELY sequential: each step depends on the result
  // of the previous one (it re-reads what the write itself just attempted, and
  // backoff only makes sense AFTER checking it is needed). There is
  // nothing to parallelize with `Promise.all()` here.
  for (;;) {
    // oxlint-disable-next-line no-await-in-loop -- see comment above
    await params.write();
    // oxlint-disable-next-line no-await-in-loop -- see comment above
    const rows = await params.reread();
    if (params.verify(rows)) {
      return;
    }
    if (Date.now() >= deadline) {
      throw new Error(
        `[powersync] writeVerified: ${params.describe} no reflejó la escritura tras ${String(budgetMs)}ms ` +
          `(la vista de PowerSync no llegó a sincronizarse). La escritura NO se dio por hecha.`,
      );
    }
    // oxlint-disable-next-line no-await-in-loop -- intentional backoff, see comment above
    await sleep(nextDelay(attempt, baseDelayMs));
    attempt += 1;
  }
}

/**
 * Waits (without writing) for `reread` to return rows that satisfy `verify`,
 * with the same backoff/budget as {@link writeVerified}. Use it BEFORE
 * opening a transaction whose UPDATE depends on a row created by a
 * previous mutation that might not have come down from the checkpoint yet.
 */
export async function waitForRowsPresent<Row>(params: {
  reread: () => Promise<Row[]>;
  verify: (rows: Row[]) => boolean;
  describe: string;
  options?: RowVerifyOptions;
}): Promise<Row[]> {
  const budgetMs = params.options?.budgetMs ?? DEFAULT_BUDGET_MS;
  const baseDelayMs = params.options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const deadline = Date.now() + budgetMs;
  let attempt = 0;

  // Same as in `writeVerified`: sequential on purpose (poll → backoff →
  // poll), nothing to parallelize.
  for (;;) {
    // oxlint-disable-next-line no-await-in-loop -- see comment above
    const rows = await params.reread();
    if (params.verify(rows)) {
      return rows;
    }
    if (Date.now() >= deadline) {
      throw new Error(
        `[powersync] waitForRowsPresent: ${params.describe} nunca apareció en la vista tras ${String(budgetMs)}ms.`,
      );
    }
    // oxlint-disable-next-line no-await-in-loop -- intentional backoff, see comment above
    await sleep(nextDelay(attempt, baseDelayMs));
    attempt += 1;
  }
}
